import { Env } from 'infra/env'
import { action, computed, makeObservable, observable } from 'mobx'
import { Subscription, distinctUntilChanged } from 'rxjs'
import CouponStore from 'billing/CouponStore'
import UserCoupon from 'billing/models/UserCoupon'
import { SearchableUser } from 'iam/models/SearchableUser'
import TeamMember from 'iam/models/TeamMember'
import { UserDataType } from 'iam/models/UserDataType'
import Workspace from 'iam/models/Workspace'
import TeamMemberStore from 'iam/TeamMemberStore'
import { Membership } from 'iam/WorkspaceMembershipStore'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { ShowForWorkspace } from '../AddUserToTeamDialog/AddUserToTeamViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import MemberStore from 'iam/MemberStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import Member from 'iam/models/Member'
import { isNotNil } from 'utils/object-util'
import JobStatsStore from 'jobs/job-stats/JobStatsStore'
import { sum } from 'lodash'
import { JobTotalStatusAggregationsDTO } from 'jobs/job-stats/dtos'
import TabController from 'infra/TabController'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { task } from 'mobx-task'
const { copy } = require('clipbrd')

/**
 * View Model for the Memberships tab.
 */
export class MembershipsTabViewModel {
  /**
   * Subscription to stream of changes to the memberships tab view.
   */
  private subscription = new Subscription()

  /**
   * Serch text to filter memberships by.
   */
  @observable
  searchText = new SearchTextViewModel()

  /**
   * Tab controller for the Membership Tabs.
   */
  tabController = new TabController<MembershipTabs>(() => [
    {
      tab: MembershipTabs.Active,
      title: 'Active',
      hasError: () => false,
    },
    {
      tab: MembershipTabs.Inactive,
      title: 'Inactive',
      hasError: () => false,
    },
  ])

  /**
   * The currently selected workspace membership.
   */
  @observable
  selectedMembership: Nullable<Membership> = null

  /**
   * Number of active jobs for the currently selected workspace membership.
   */
  @observable
  jobTotals: Partial<MembershipJobTotals> = { totalJobs: 0, activeJobs: 0 }

  /**
   * Coupons for the currently selected workspace membership.
   */
  @observable
  coupons: UserCoupon[] = []

  /**
   * List of team members for the currently selected workspace membership.
   */
  @observable
  teamMembers: TeamMember[] = []

  /**
   * Stream of changes to the memberships tab view.
   */
  private membershipsTab$ = toStream(() => this.selectedMembership, false).pipe(
    distinctUntilChanged(),
    tapAction((membership) => {
      if (membership === null) {
        this.reset()
        return
      }

      this.fetchJobTotalsForMembership(membership)
      if (!this.isPro) {
        this.fetchCouponsForMembership(membership)
      }
      this.fetchTeamMembersForMembership(membership)
    }),
  )

  constructor(
    private _user: () => SearchableUser,
    private _showAddUserToTeamDialogForWorkspace: ShowForWorkspace,
    private _showAddUserToWorkspaceDialog: () => void,
    private _showJoinWorkspaceAsAdminDialog: () => void,
    private _canJoinWorkspaceAsAdmin: () => boolean,
    private _showAddCustomerToWorkspaceDialog: () => void,
    private couponStore: CouponStore,
    private jobSearchStore: JobSearchStore,
    private teamMemberStore: TeamMemberStore,
    public editUserRole: (user: SearchableUser, workspace: Workspace) => void,
    public removeFromWorkspace: (
      user: SearchableUser,
      workspace: Workspace,
    ) => void,
    public restoreWorkspaceMembership: (
      user: SearchableUser,
      workspace: Workspace,
    ) => void,
    public manageCapacity: (user: SearchableUser, workspace: Workspace) => void,
    public editSkills: (user: SearchableUser, workspace: Workspace) => void,
    private memberStore: MemberStore,
    private getCurrentMember: GetCurrentMember,
    private readonly newAlert: AlertFactory,
    private logout: () => Promise<void>,
    private jobStatsStore: JobStatsStore,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.subscription = this.membershipsTab$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()
  }

  /**
   * The current member.
   */
  @computed
  get currentMember() {
    return this.getCurrentMember()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * Whether the currently selected user is a provider.
   */
  @computed
  get isPro() {
    return this.user.userDataType === UserDataType.Pro
  }

  /**
   * The currently selected user's workspace memberships.
   */
  @computed
  get memberships() {
    return this.user.memberships
  }

  /**
   * The workspace memberships count based on the currently selected tab.
   */
  @computed
  get membershipsCount() {
    switch (this.tabController.current.tab) {
      case MembershipTabs.Active:
        return this.activeMemberships.length

      case MembershipTabs.Inactive:
        return this.inactiveMemberships.length
    }
  }

  /**
   * Whether the search text is empty.
   */
  @computed
  get isSearchTextEmpty() {
    return this.searchText.value === ''
  }

  @computed
  get activeMemberships() {
    const activeMemberships = this.memberships.filter(
      (membership) => !membership.member.inactive,
    )

    if (this.isSearchTextEmpty) {
      return activeMemberships
    }

    const searchText = this.searchText.value.toLowerCase()

    return activeMemberships.filter((membership) =>
      membership.workspace.name.toLowerCase().includes(searchText),
    )
  }

  @computed
  get inactiveMemberships() {
    const inactiveMemberships = this.memberships.filter(
      (membership) => membership.member.inactive,
    )

    if (this.isSearchTextEmpty) {
      return inactiveMemberships
    }

    const searchText = this.searchText.value.toLowerCase()

    return inactiveMemberships.filter((membership) =>
      membership.workspace.name.toLowerCase().includes(searchText),
    )
  }

  /**
   * Whether the memberships tab sidebar is open.
   */
  @computed
  get isSidebarOpen() {
    return this.selectedMembership !== null
  }

  /**
   * Whether the current user is part of the selected workspace.
   */
  @computed
  get workspaceAccess(): Record<string, null | AdminAccess> {
    return Object.fromEntries(
      this.memberships.map(({ workspace }) => {
        let member: Member | null

        try {
          member = this.getCurrentMember(workspace.id)
        } catch (e) {
          return [workspace.id, null]
        }

        if (isNotNil(member)) {
          if (member.inactive) {
            return [workspace.id, null]
          }

          return [
            workspace.id,
            {
              canImpersonateAsClient: member.hasPermission(
                'ADMIN_CLIENT_IMPERSONATE',
              ),
              canImpersonateAsPro: member.hasPermission(
                'ADMIN_PROVIDER_IMPERSONATE',
              ),
              canAddUserToTeam: member.hasPermission(
                'ADMIN_MANAGE_TEAM_MEMBER',
              ),
              canEditUserRole: member.hasPermission('ADMIN_USER_ROLES_MODIFY'),
              canEditUserSkills: member.hasPermission('ADMIN_SKILLS_MANAGE'),
              canManageUserCapacity: member.hasPermission('ADMIN_USER_MODIFY'),
              canManageWorkspaceMemberships:
                member.hasPermission('ADMIN_USER_MODIFY'),
            },
          ]
        }

        return [workspace.id, null]
      }),
    )
  }

  /**
   * Sets the current membership tab.
   */
  @action.bound
  setCurrentMembershipTab(tab: MembershipTabs) {
    this.tabController.change(tab)
    this.clearFilters()
  }

  /**
   * Sets the currently selected workspace membership.
   */
  @action.bound
  setSelectedMembership(membership: Nullable<Membership>) {
    this.selectedMembership = membership
  }

  /**
   * Sets the number of active jobs for the currently selected workspace membership.
   */
  @action.bound
  setJobTotals(jobTotals: JobTotalStatusAggregationsDTO) {
    this.jobTotals = {
      totalJobs: sum(Object.values(jobTotals)),
      activeJobs: jobTotals.claimed + jobTotals.idle,
      claimedJobs: jobTotals.claimed,
      onHoldJobs: jobTotals.onHold,
      idleJobs: jobTotals.idle,
      closedJobs: jobTotals.closed,
      underConstructionJobs: jobTotals.underConstruction,
      unclaimedJobs: jobTotals.unclaimed,
      infoGatheringJobs: jobTotals.infoGathering,
    }
  }

  /**
   * Sets the coupons for the currently selected workspace membership.
   */
  @action.bound
  setCoupons(coupons: UserCoupon[]) {
    this.coupons = coupons
  }

  /**
   * Sets the team members for the currently selected workspace membership.
   */
  @action.bound
  setTeamMembers(teamMembers: TeamMember[]) {
    this.teamMembers = teamMembers
  }

  /**
   * Opens the memberships tab sidebar and sets the current workspace membership.
   */
  @action.bound
  openSidebar(membership: Membership) {
    if (this.selectedMembership === membership) {
      this.closeSidebar()
      return
    }

    this.setSelectedMembership(membership)
  }

  /**
   * Closes the memberships tab sidebar.
   */
  @action.bound
  closeSidebar() {
    this.setSelectedMembership(null)
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.setSelectedMembership(null)
    this.setCoupons([])
    this.setTeamMembers([])
    this.jobTotals = { totalJobs: 0, activeJobs: 0 }
    this.clearFilters()
  }

  /**
   * Impersonates the selected user as either a client or a provider.
   */
  async impersonate(workspace: Workspace, target: 'client' | 'provider') {
    const domain = workspace.domains[0]?.domain
    const waitFn = `document.write('Please wait, generating impersonation URL.')`
    const newTab = window.open('javascript:' + waitFn, '_blank')

    const clientPortalBase = domain
      ? `${window.location.protocol}//${domain}`
      : Env.CLIENT_PORTAL_URL
    const clientPortal = `${clientPortalBase}/auth/callback`
    const portal =
      target === 'client' ? clientPortal : Env.IMPERSONATED_PROVIDER_PORTAL_URL

    await this.memberStore
      .impersonate(
        Id.assert(this.user.publicId),
        Id.assert(workspace.id),
        portal,
      )
      .then(async (result) => {
        newTab?.location.assign(result.url)
      })
      .catch((err) => {
        this.newAlert().fromError(err).show().autoDismiss()
        newTab?.close()
      })
  }

  /**
   * Generates Magic Link for a given user and copies it to the clipboard.
   */
  generateMagicLink = task.resolved(
    async (userPublicId: Id, workspaceId: Id) => {
      const copyToClipboard = (value: string) => {
        const success = copy(value)
        const msg = {
          message: success
            ? 'The Magic Link was copied to clipboard!'
            : 'Oh, seems like your browser does not support copying to the clipboard.',
          state: success ? AlertState.Success : AlertState.Warning,
        }

        this.newAlert()
          .withState(msg.state)
          .saying(msg.message)
          .show()
          .autoDismiss()
      }

      await this.memberStore
        .generateMagicLink(Id.assert(userPublicId), Id.assert(workspaceId), {
          type: 'DASHBOARD',
        })
        .then(async (result) => {
          copyToClipboard(result.url)
        })
        .catch((err) => {
          this.newAlert().fromError(err).show().autoDismiss()
        })
    },
  )

  /**
   * Shows the add user to team dialog for the currently selected workspace.
   */
  showAddUserToTeamDialogForWorkspace(workspace: Workspace) {
    this._showAddUserToTeamDialogForWorkspace(workspace)
  }

  /**
   * Shows the add user to workspace dialog.
   */
  showAddUserToWorkspaceDialog() {
    this._showAddUserToWorkspaceDialog()
  }

  /**
   * Whether the current user can join workspaces as admin.
   */
  get canJoinWorkspaceAsAdmin() {
    return this._canJoinWorkspaceAsAdmin()
  }

  /**
   * Shows the join workspace as admin dialog.
   */
  showJoinWorkspaceAsAdminDialog() {
    this._showJoinWorkspaceAsAdminDialog()
  }

  /**
   * Whether the currently selected user is a workspace customer.
   */
  @computed
  get isCustomerUser() {
    return this._user().isWorkspaceCustomer
  }

  /**
   * Shows the add customer to workspace dialog.
   */
  showAddCustomerToWorkspaceDialog() {
    this._showAddCustomerToWorkspaceDialog()
  }

  /**
   * Fetches the number of active and total jobs for the given workspace membership.
   */
  private async fetchJobTotalsForMembership(membership: Membership) {
    const userPublicId = membership.member.user.publicId
    const workspaceId = membership.workspace.id

    const projection = await this.jobStatsStore.getJobTotalStatusAggregations(
      userPublicId,
      workspaceId,
    )

    this.setJobTotals(projection)
  }

  /**
   * Fetches the coupons for the given workspace membership.
   */
  private async fetchCouponsForMembership(membership: Membership) {
    const redeemedCoupons = await this.couponStore
      .fetchUserCoupons(membership.member)
      .then((coupons) => coupons.filter((coupon) => coupon.redeemed))

    this.setCoupons(redeemedCoupons)
  }

  /**
   * Fetches the team members for the given workspace membership.
   */
  private async fetchTeamMembersForMembership(membership: Membership) {
    const userPublicId = membership.member.user.publicId
    const workspaceId = membership.workspace.id

    const teamMembers =
      await this.teamMemberStore.fetchUserTeamMembersForWorkspace({
        user_id: userPublicId,
        workspace_id: workspaceId,
      })

    this.setTeamMembers(teamMembers)
  }
}

export const enum MembershipTabs {
  Active,
  Inactive,
}

export type AdminAccess = {
  canImpersonateAsClient: boolean
  canImpersonateAsPro: boolean
  canAddUserToTeam: boolean
  canEditUserRole: boolean
  canEditUserSkills: boolean
  canManageUserCapacity: boolean
  canManageWorkspaceMemberships: boolean
}

type MembershipJobTotals = {
  /**
   * Number of total jobs claimed or idle.
   */
  activeJobs: number

  /**
   * Number of total jobs for the currently selected workspace membership.
   */
  totalJobs: number

  /**
   * Number of total jobs claimed.
   */
  claimedJobs: number

  /**
   * Number of total jobs on hold.
   */
  onHoldJobs: number

  /**
   * Number of total jobs idle.
   */
  idleJobs: number

  /**
   * Number of total jobs closed.
   */
  closedJobs: number

  /**
   * Number of total jobs under construction.
   */
  underConstructionJobs: number

  /**
   * Number of total jobs unclaimed.
   */
  unclaimedJobs: number

  /**
   * Number of total jobs in info gathering.
   */
  infoGatheringJobs: number
}
